import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const ExternalIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const partners = [
  {
    name: 'Beam Center',
    description: 'Empowering PS 96 students through project-based learning that fuses art, engineering, and technology. Students build large-scale, real-world projects and develop confidence as makers and creators.',
    tag: 'Arts & Technology',
    href: '/beam-center',
    external: false,
    images: [
      { src: '/assets/images/partnerships/beancenter2.jpeg', fit: 'cover' },
    ],
  },
  {
    name: 'The Jewish Children\'s Museum',
    description: 'Located in Brooklyn, the Jewish Children\'s Museum offers immersive, interactive exhibits that inspire curiosity and cultural appreciation. PS 96 students visit to explore Jewish history, values, and stories in a joyful, hands-on environment.',
    tag: 'Arts & Culture',
    href: 'https://www.jcm.museum/',
    external: true,
    images: [
      { src: '/assets/images/partnerships/jewishmuseum1.jpeg', fit: 'cover' },
      { src: '/assets/images/partnerships/jewishmuseum2.jpeg', fit: 'cover' },
      { src: '/assets/images/partnerships/jewishmuseum3.jpeg', fit: 'cover' },
    ],
  },
  {
    name: 'Asphalt Green',
    description: 'Asphalt Green provides swimming instruction for our 2nd grade students, ensuring that every PS 96 Lion has access to high-quality aquatics education. Their expert instructors build water safety skills and a lifelong love of swimming.',
    tag: 'Swimming — 2nd Grade',
    href: '/asphalt-green',
    external: false,
    images: [
      { src: '/assets/images/partnerships/beancenter1.jpg', fit: 'cover' },
      { src: '/assets/images/partnerships/asphaltgreen.svg', fit: 'contain' },
      { src: '/assets/images/partnerships/safeswimnyc.webp', fit: 'contain' },
    ],
  },
  {
    name: 'Global Kids',
    description: 'A nonprofit focused on youth development, global education, and civic engagement. Global Kids has been a valued partner at PS 96, running programming that connects our students to global issues and builds their voices as young leaders.',
    tag: 'Youth Development',
    href: '/global_kids_inc',
    external: false,
    images: [
      { src: '/assets/images/partnerships/globalkids.png', fit: 'contain' },
    ],
  },
];

const fitClass = (fit, pad) =>
  fit === 'cover' ? 'object-cover' : `object-contain ${pad}`;

const PartnerImage = ({ image, name, pad }) => (
  <img
    src={image.src}
    alt={name}
    className={`w-full h-full ${fitClass(image.fit, pad)}`}
  />
);

const PartnerCard = ({ p }) => {
  const count = p.images.length;

  return (
    <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-[#fdbb2e]/40 hover:-translate-y-0.5 transition-all duration-200 flex flex-col">
      {/* Image area */}
      <div className="relative h-52 bg-gray-50 overflow-hidden border-b border-gray-100">
        {count >= 3 ? (
          <div className="flex h-full">
            <div className="w-1/2 h-full overflow-hidden border-r border-gray-100">
              <PartnerImage image={p.images[0]} name={p.name} pad="p-4" />
            </div>
            <div className="w-1/2 h-full flex flex-col">
              <div className="h-1/2 overflow-hidden border-b border-gray-100">
                <PartnerImage image={p.images[1]} name={p.name} pad="p-3" />
              </div>
              <div className="h-1/2 overflow-hidden">
                <PartnerImage image={p.images[2]} name={p.name} pad="p-3" />
              </div>
            </div>
          </div>
        ) : count === 2 ? (
          <div className="flex h-full">
            <div className="w-1/2 h-full overflow-hidden border-r border-gray-100">
              <PartnerImage image={p.images[0]} name={p.name} pad="p-4" />
            </div>
            <div className="w-1/2 h-full overflow-hidden">
              <PartnerImage image={p.images[1]} name={p.name} pad="p-4" />
            </div>
          </div>
        ) : (
          <PartnerImage image={p.images[0]} name={p.name} pad="p-8" />
        )}
      </div>

      {/* Content */}
      <div className="p-7 flex flex-col flex-1">
        <div className="mb-3">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#00213a] bg-[#fdbb2e]/15 px-3 py-1 rounded-full">
            {p.tag}
          </span>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-3 leading-snug">{p.name}</h2>
        <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-6">{p.description}</p>
        <a
          href={p.href}
          target={p.external ? '_blank' : undefined}
          rel={p.external ? 'noopener noreferrer' : undefined}
          className="inline-flex items-center gap-2 text-sm font-bold text-[#00213a] group-hover:gap-3 transition-all duration-200"
        >
          {p.external ? 'Visit website' : 'Learn more'}
          {p.external ? <ExternalIcon /> : <ArrowRightIcon />}
        </a>
      </div>
    </div>
  );
};

const PartnershipsPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Community</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Our Partnerships
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            PS 96 is proud to collaborate with outstanding organizations that enrich the lives of our students and families.
          </p>
        </div>
      </section>

      {/* Partner cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-16 mb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {partners.map((p) => (
            <PartnerCard key={p.name} p={p} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Interested in Partnering with PS 96?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            We welcome organizations that share our commitment to student success and community empowerment.
          </p>
          <a
            href="/contact-us"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#fdbb2e] text-[#00213a] font-black rounded-xl hover:bg-[#ffd04e] transition-colors duration-200 shadow-lg"
          >
            Contact Us <ArrowRightIcon />
          </a>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default PartnershipsPage;
